<template>
  <app-content class="report-view">
    <s-loader
      v-if="generalLoading"
      size="lg"
    />
    <b-tabs
      v-else-if="tabs.length"
      v-model="activeTab"
      class="w-100"
    >
      <b-tab
        v-for="tab in tabs"
        :key="tab.name"
        :title="tab.title">
        <generic-diagnostics-history-tab
          :loading="loadings[tab.name]"
          :diagnostics-history.sync="diagnosticsHistory[tab.name]"
          :module="tab.name"
          :filter="pagedFilter.filter"
          @refresh-data="refreshData(tab.name, $event)"
        />
      </b-tab>
    </b-tabs>
    <s-empty-list v-else/>
  </app-content>
</template>

<script>
import { formatter as dateTimeFormatter } from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';
import storageManager from '@/services/sessionStorageManager';
import { Modules } from 'sigma-mappings';
import DiagnosticsService from '@/domain/services/generalPanel/reports/diagnostics';
import GenericDiagnosticsHistoryTab
  from '@/domain/components/generalPanel/reports/diagnosticsHistory/GenericDiagnosticsHistoryTab';

export default {
  components: {
    GenericDiagnosticsHistoryTab,
  },
  data: () => ({
    activeTab: 0,
    pagedFilter: {
      pageSize: 100,
      page: 1,
      filter: {},
    },
    diagnosticsHistory: {
      chromatography: {},
      'physical-chemical': {},
    },
    loadings: {
      chromatography: false,
      'physical-chemical': false,
    },
    tabs: [],
    generalLoading: false,
    totalRecords: 0,
    history: [],
  }),
  created() {
    this.getSessionStorage();
    this.getDiagnostics();
  },
  computed: {
    allTabs() {
      return [
        {
          name: Modules.CHROMATOGRAPHY,
          title: this.$t('enums.engineering.chromatography.name'),
        },
        {
          name: Modules.PHYSICAL_CHEMICAL,
          title: this.$t('enums.engineering.physical-chemical.name'),
        },
      ];
    },
  },
  methods: {
    getFilteredTabs() {
      return this.allTabs.filter(tab => this.showTab(tab));
    },
    showTab(tab) {
      const items = this.diagnosticsHistory[tab.name];
      return !!(items.data || []).length;
    },
    onRefreshSearch(input) {
      this.pagedFilter = input;
      this.refreshData();
    },
    getDiagnostics() {
      const modules = this.pagedFilter.filter.modules || [];
      const promises = modules.map((module) => {
        if (module === Modules.CHROMATOGRAPHY) {
          return this.getChromatographyDiagnostics(this.pagedFilter);
        }
        return this.getPhysicalChemicalDiagnostics(this.pagedFilter);
      });
      this.handleGeneralPromises(promises);
    },
    handleGeneralPromises(promises) {
      this.generalLoading = true;
      Promise.allSettled([...promises])
        .then(() => {
          this.tabs = this.getFilteredTabs();
          if (this.tabs.length) {
            this.$pushAlert({ code: 'MS011' });
          }
        })
        .finally(async () => {
          await this.$nextTick();
          this.generalLoading = false;
          if (!this.tabs.length) {
            this.$pushAlert({ code: 'MA009' });
          }
        });
    },
    getChromatographyDiagnostics(pagedFilter) {
      this.loadings[Modules.CHROMATOGRAPHY] = true;
      return DiagnosticsService.chromatographyDiagnosticsList(pagedFilter)
        .then((response) => {
          this.diagnosticsHistory[Modules.CHROMATOGRAPHY] = {
            ...response,
            data: this.dataHandler(response),
          };
        }).finally(() => {
          this.loadings[Modules.CHROMATOGRAPHY] = false;
        });
    },
    getPhysicalChemicalDiagnostics(pagedFilter) {
      this.loadings[Modules.PHYSICAL_CHEMICAL] = true;
      return DiagnosticsService.physicalChemicalDiagnosticsList(pagedFilter)
        .then((response) => {
          this.diagnosticsHistory[Modules.PHYSICAL_CHEMICAL] = {
            ...response,
            data: this.dataHandler(response),
          };
        }).finally(() => {
          this.loadings[Modules.PHYSICAL_CHEMICAL] = false;
        });
    },
    refreshData(moduleName, typePagedFilter) {
      const filter = { ...this.pagedFilter.filter, ...typePagedFilter.filter };
      const pagedFilter = { ...typePagedFilter, filter };
      this.loadings[moduleName] = true;
      if (moduleName === Modules.CHROMATOGRAPHY) {
        this.getChromatographyDiagnostics(pagedFilter);
        return;
      }
      this.getPhysicalChemicalDiagnostics(pagedFilter);
    },
    dataHandler(response) {
      return response.data
        .map(it => ({
          ...it,
          sampleDateTime: this.getDateTime(it.sampleDateTime),
          analysisTime: this.getTime(it.analysisDateTime),
          analysisDate: this.getDate(it.analysisDateTime),
        }));
    },
    getDateTime(date) {
      if (!date) return null;
      return dateTimeFormatter(date, formats.DATETIME_SHORT_WITH_SECONDS, timezones.UTC);
    },
    getDate(date) {
      if (!date) return null;
      return dateTimeFormatter(date, formats.DATE_SHORT, timezones.UTC);
    },
    getTime(date) {
      if (!date) return null;
      return dateTimeFormatter(date, formats.TIME_24_WITH_SECONDS, timezones.UTC);
    },
    getSessionStorage() {
      if (!this.$route.query) return;
      const storageItem = storageManager.getItem(this.$route.query.filter);
      this.pagedFilter.filter = { ...this.tryJsonParse(storageItem), ...this.pagedFilter.filter };
    },
    tryJsonParse(item) {
      try {
        return JSON.parse(item);
      } catch (e) {
        this.$pushAlert({
          message: this.$t('reports.filter-error'),
          type: 'warning',
        });
        return {};
      }
    },
  },
};
</script>
<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.report-view
  +media-xs
    font-size: .9rem
    &__table
      overflow-y: scroll
      height: calc(100vh - 265px)
    &__headers-alarm-name
      display: inline-flex
      align-items: center
      padding-left: 0
      +media-xs
        flex-direction: column
    &__icon-value
      align-items: center
      display: inline-flex
      margin-right: 20px
    &__alarm-key
      font-weight: bold
    &__margin-col
      margin-right: 12px
      +media-max-sm
        margin: 0
    &__offline-alarm-infos, &__offline-alarm-action-infos
      display: grid
      grid-gap: 10px
      grid-template-columns: repeat(4, 280px)
      margin: 0 0 5px 50px
      +media-max-sm
        margin-left: 0
        grid-template-columns: auto
      +media-md
        margin-left: 0
        grid-template-columns: repeat(2, auto)
      +media-lg
        margin-left: 0
        grid-template-columns: repeat(3, auto)
    &__alarm-description-wrapper
      display: flex
      +media-min-xl
        margin-left: 50px
      span
        word-break: break-all
    &__alarm-infos
      display: grid
      grid-gap: 10px
      grid-template-columns: repeat(5, auto)
      margin: 0 0 5px 50px
      +media-max-sm
        grid-template-columns: auto
      +media-md
        margin-left: 0
        grid-template-columns: repeat(3, auto)
      +media-lg
        margin-left: 0
        grid-template-columns: repeat(4, auto)
      +media-min-2xl
        div
          min-width: 140px
    ::v-deep.accordion-panel__title-container
      width: 100%
    ::v-deep.app-content__main-content
      padding: 20px 20px 20px 15px
</style>
